import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { User, Key, Shield, Check, AlertCircle } from 'lucide-react'
import { useAuthStore } from '../stores/auth'
import { auth } from '../api/client'

export default function Account() {
  const { user } = useAuthStore()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const changePasswordMutation = useMutation({
    mutationFn: () => auth.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setSuccessMessage('Password changed successfully!')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setSuccessMessage(''), 5000)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSuccessMessage('')

    if (newPassword !== confirmPassword) {
      return
    }

    if (newPassword.length < 6) {
      return
    }

    changePasswordMutation.mutate()
  }

  const passwordsMatch = newPassword === confirmPassword || confirmPassword === ''
  const passwordLongEnough = newPassword.length >= 6 || newPassword === ''
  const canSubmit = currentPassword && newPassword && confirmPassword && passwordsMatch && passwordLongEnough

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
          <User size={24} className="text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Account</h1>
          <p className="text-dark-400 text-sm">Manage your account settings</p>
        </div>
      </div>

      {/* User Info Card */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <User size={20} />
          Profile
        </h2>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center">
              <span className="text-2xl font-bold text-white">
                {user?.username.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-lg font-medium">{user?.username}</p>
              {user?.is_admin && (
                <p className="text-sm text-primary-400 flex items-center gap-1">
                  <Shield size={14} />
                  Administrator
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Key size={20} />
          Change Password
        </h2>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400 flex items-center gap-2">
            <Check size={18} />
            {successMessage}
          </div>
        )}

        {changePasswordMutation.error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
            <AlertCircle size={18} />
            {(changePasswordMutation.error as any)?.response?.data?.error || 'Failed to change password'}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input w-full"
              placeholder="Enter your current password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input w-full"
              placeholder="Enter new password (min 6 characters)"
              required
              minLength={6}
            />
            {newPassword && !passwordLongEnough && (
              <p className="text-xs text-red-400 mt-1">Password must be at least 6 characters</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input w-full"
              placeholder="Confirm new password"
              required
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit || changePasswordMutation.isPending}
            className="btn btn-primary w-full sm:w-auto"
          >
            {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
